import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../config/paths';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { ForceGraph2D } from 'react-force-graph';

interface RewardTreeSummary {
    id: string;
    child_number: number;
    amount: number;
    createdAt: string;
}

interface UserReferral {
    id: string;
    username: string;
    parent_user_id: string | null;
    user_fullpath: string;
    depth: number;
    is_debit_card_purchased: boolean;
    createdAt: string;
    reward_trees: RewardTreeSummary[];
    link_type: string | null;
}

interface GetUserReferralsResponse {
    referrals: UserReferral[];
}

interface GraphNode {
    id: string;
    username: string;
    depth: number;
    is_debit_card_purchased: boolean;
    isRoot?: boolean;
    link_type?: string | null;
    x?: number;
    y?: number;
}

interface GraphLink {
    source: string;
    target: string;
    color: string;
}

interface SelectedNodeInfo {
    node: GraphNode;
    totalDescendants: number;
    directReferralChildren: number;
    distributorCodeUsersWithin7Levels: number;
}

interface GraphData {
    nodes: GraphNode[];
    links: GraphLink[];
}

const createGraphData = (currentUser: { id: string; username: string; email: string; is_debit_card_purchased: boolean }, referrals: UserReferral[]): GraphData => {
    // Create root node (current user)
    const rootNode: GraphNode = {
        id: currentUser.id,
        username: currentUser.username,
        depth: 0,
        is_debit_card_purchased: currentUser.is_debit_card_purchased,
        isRoot: true
    };

    // Create user nodes (sort by depth, then link type, then user_fullpath)
    const userNodes = [...referrals]
        .sort((a, b) => {
            // First sort by depth
            if (a.depth !== b.depth) {
                return a.depth - b.depth;
            }

            // Then sort by link type
            // Link type priority: distributor_code > referral_code > direct > null
            const getLinkTypePriority = (linkType: string | null): number => {
                switch (linkType) {
                    case 'distributor_code': return 1;
                    case 'referral_code': return 2;
                    case 'direct': return 3;
                    default: return 4;
                }
            };

            const aPriority = getLinkTypePriority(a.link_type);
            const bPriority = getLinkTypePriority(b.link_type);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // Finally sort by user_fullpath
            return a.user_fullpath.localeCompare(b.user_fullpath);
        })
        .map(user => ({
            id: user.id,
            username: user.username,
            depth: user.depth,
            is_debit_card_purchased: user.is_debit_card_purchased,
            link_type: user.link_type,
        }));

    // Combine all nodes (root node first)
    const nodes = [rootNode, ...userNodes];

    // Create parent-child links
    const links: GraphLink[] = [];

    // Map to track parent-child relationships
    const childToParentMap = new Map<string, string>();

    // Build the parent-child map
    referrals.forEach(user => {
        if (user.parent_user_id) {
            childToParentMap.set(user.id, user.parent_user_id);
        }
    });

    // Create links based on the map
    referrals.forEach(user => {
        const parentId = childToParentMap.get(user.id);
        if (parentId) {
            links.push({
                source: parentId,
                target: user.id,
                color: '#000000'
            });
        } else {
            // If no parent found in the map, connect to the root node (current user)
            links.push({
                source: rootNode.id,
                target: user.id,
                color: '#000000'
            });
        }
    });

    return { nodes, links };
};

const useUserReferrals = () => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<GetUserReferralsResponse | null>(null);

    const fetchReferrals = useCallback(async () => {
        if (!user || !accessToken) {
            setError('Authentication information not found');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/users/referrals', {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch data');
            }

            const responseData: GetUserReferralsResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken]);

    useEffect(() => {
        fetchReferrals();
    }, [fetchReferrals]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchReferrals,
    };
};

export const ReferralTreePage = () => {
    const [user] = useAtom(userAtom);
    const { data, isLoading, error } = useUserReferrals();
    const [graphSize, setGraphSize] = useState({ width: 0, height: 0 });
    const [selectedNodeInfo, setSelectedNodeInfo] = useState<SelectedNodeInfo | null>(null);
    const graphContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateGraphSize = () => {
            if (graphContainerRef.current) {
                const rect = graphContainerRef.current.getBoundingClientRect();
                setGraphSize({
                    width: rect.width,
                    height: rect.height
                });
            }
        };

        updateGraphSize();
        window.addEventListener('resize', updateGraphSize);
        return () => window.removeEventListener('resize', updateGraphSize);
    }, []);

    const graphData = useMemo<GraphData>(() => {
        if (!data?.referrals || !user) return { nodes: [], links: [] };
        return createGraphData(user, data.referrals);
    }, [data, user]);

    // Calculate statistics by depth
    const { depthStats, totalUsers, verifiedUsers } = useMemo(() => {
        if (!data?.referrals) return { depthStats: [], totalUsers: 0, verifiedUsers: 0 };

        const stats = new Map<number, number>();
        let maxDepth = 0;
        let verifiedCount = 0;

        // Count users by depth (exclude depth 0)
        data.referrals.forEach(user => {
            stats.set(user.depth, (stats.get(user.depth) || 0) + 1);
            maxDepth = Math.max(maxDepth, user.depth);
            if (user.is_debit_card_purchased) {
                verifiedCount++;
            }
        });

        // Create stats array from depth 1 to max depth
        const depthStats = Array.from({ length: maxDepth }, (_, i) => ({
            depth: i + 1,
            count: stats.get(i + 1) || 0,
        }));

        return {
            depthStats,
            totalUsers: data.referrals.length,
            verifiedUsers: verifiedCount
        };
    }, [data]);

    if (!user) {
        return <Navigate to={paths.auth.login.path} replace />;
    }

    return (
        <div className="max-w-[1600px] mx-auto">
            <h1 className="text-2xl font-bold mb-4">Referral Tree</h1>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h2 className="font-semibold mb-2">Legend</h2>
                <ul className="space-y-1 text-sm">
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#1E40AF] mr-2"></div>
                        <span>Blue → You</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#4CAF50] mr-2"></div>
                        <span>Green → Card payment confirmed</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#9E9E9E] mr-2"></div>
                        <span>Gray → Card payment pending</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 border-2 border-[#FF0000] rounded-full mr-2"></div>
                        <span>Red border → Registered via distributor link</span>
                    </li>
                </ul>
            </div>
            {selectedNodeInfo && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold mb-2">Selected User Information</h3>
                    <div className="space-y-2 text-sm">
                        <p>Username: {selectedNodeInfo.node.username}</p>
                        <p>Registration Type: <span className="font-semibold">
                            {selectedNodeInfo.node.link_type === 'direct' ? 'TOP Distributor' :
                                selectedNodeInfo.node.link_type === 'referral_code' ? 'Registered via direct referral link' :
                                    selectedNodeInfo.node.link_type === 'distributor_code' ? 'Registered via distributor link' :
                                        'Normal registration'}
                        </span></p>
                        <div className="border-t border-blue-200 my-2"></div>
                        <p>Total descendants: <span className="font-semibold">{selectedNodeInfo.totalDescendants} users</span></p>
                        <p>Direct referral users: <span className="font-semibold">{selectedNodeInfo.directReferralChildren} users</span></p>
                        <p>Distributor code registrations within 7 levels: <span className="font-semibold">{selectedNodeInfo.distributorCodeUsersWithin7Levels} users</span></p>
                    </div>
                </div>
            )}
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left side: Network graph (Desktop: 70%) */}
                <div className="w-full md:w-[70%] bg-white shadow rounded-lg p-6">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-[600px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <div className="h-[600px] w-full overflow-hidden" ref={graphContainerRef}>
                            <ForceGraph2D
                                graphData={graphData}
                                nodeLabel={(node: GraphNode) =>
                                    node.isRoot ? 'You' : node.username
                                }
                                nodeRelSize={4}
                                linkDirectionalArrowLength={3}
                                linkDirectionalArrowRelPos={1}
                                width={graphSize.width}
                                height={graphSize.height}
                                linkColor={(link: GraphLink) => link.color}
                                dagMode="td"
                                dagLevelDistance={50}
                                linkDirectionalParticles={1}
                                linkDirectionalParticleWidth={4}
                                nodeCanvasObject={(node: GraphNode, ctx, globalScale) => {
                                    const fontSize = 12 / globalScale;
                                    ctx.font = `${fontSize}px Sans-Serif`;
                                    const nodeSize = 4;
                                    const borderWidth = 2;

                                    // Set node background color
                                    ctx.beginPath();
                                    ctx.fillStyle = node.isRoot
                                        ? '#1E40AF'  // Blue for root (current user)
                                        : node.is_debit_card_purchased
                                            ? '#4CAF50'  // Green for verified users
                                            : '#9E9E9E';  // Gray for unverified users
                                    ctx.arc(node.x!, node.y!, nodeSize, 0, 2 * Math.PI);
                                    ctx.fill();

                                    // Add red border for distributor_code users
                                    if (node.link_type === 'distributor_code') {
                                        ctx.beginPath();
                                        ctx.strokeStyle = '#FF0000';
                                        ctx.lineWidth = borderWidth / globalScale;
                                        ctx.arc(node.x!, node.y!, nodeSize + (borderWidth / globalScale), 0, 2 * Math.PI);
                                        ctx.stroke();
                                    }
                                }}
                                onNodeClick={(node) => {
                                    if (!data?.referrals) {
                                        setSelectedNodeInfo(null);
                                        return;
                                    }

                                    const clickedNode = node as GraphNode;
                                    const referrals = data.referrals;

                                    // Function to find descendants
                                    const findDescendants = (nodeId: string) => {
                                        return referrals.filter(user =>
                                            user.user_fullpath.startsWith(`${nodeId}`) ||
                                            user.user_fullpath.includes(`/${nodeId}/`) ||
                                            user.user_fullpath.endsWith(`/${nodeId}`)
                                        );
                                    };

                                    // Function to find direct children
                                    const findDirectChildren = (nodeId: string) => {
                                        return referrals.filter(user => user.parent_user_id === nodeId);
                                    };

                                    // Calculate total descendants
                                    const descendants = findDescendants(clickedNode.id);
                                    const totalDescendants = descendants.length;

                                    // Calculate direct referral children
                                    const directChildren = findDirectChildren(clickedNode.id);
                                    const directReferralChildren = directChildren.filter(
                                        child => (child.link_type === null || child.link_type === 'referral_code') && child.id !== clickedNode.id
                                    ).length;

                                    // Calculate distributor code registrations within 7 levels
                                    const distributorCodeUsersWithin7Levels = descendants.filter(desc => {
                                        const relativeDepth = desc.depth - (node as GraphNode).depth;
                                        return relativeDepth <= 7 && desc.link_type === 'distributor_code' && desc.id !== clickedNode.id;
                                    }).length;

                                    // Update selected node information
                                    setSelectedNodeInfo({
                                        node: clickedNode,
                                        totalDescendants,
                                        directReferralChildren,
                                        distributorCodeUsersWithin7Levels
                                    });

                                    // Handle node position fixing/unfixing
                                    const forceNode = node as { fx?: number; fy?: number; x?: number; y?: number };
                                    if (forceNode.fx !== undefined) {
                                        forceNode.fx = undefined;
                                        forceNode.fy = undefined;
                                    } else if (typeof forceNode.x === 'number' && typeof forceNode.y === 'number') {
                                        forceNode.fx = forceNode.x;
                                        forceNode.fy = forceNode.y;
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Right side: Depth statistics (Desktop: 30%) */}
                <div className="w-full md:w-[30%] bg-white shadow rounded-lg p-6">
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold mb-1">Users by Depth</h2>
                        <div className="text-gray-600">Total: {totalUsers} users (Verified: {verifiedUsers} users)</div>
                    </div>
                    {isLoading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {depthStats.map(({ depth, count }) => (
                                <div key={depth} className="flex justify-between items-center">
                                    <span className="text-gray-700">Depth {depth}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 bg-blue-500 rounded" style={{
                                            width: `${Math.min(count * 3, 150)}px`
                                        }}></div>
                                        <span className="text-gray-600 min-w-[3rem] text-right">
                                            {count} users
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferralTreePage;